<script lang="ts">
import { templateRef } from '@vueuse/core'
import { gsap } from 'gsap'
import type { ComponentPublicInstance, Ref } from 'vue'
import { defineComponent, inject, onMounted, readonly, ref, watch } from 'vue'

import Logo from '@/assets/svg/monogrid.svg'
import OverlayTexts from '@/components/atoms/OverlayTexts.vue'
import LayerLinearGrid from '@/components/molecules/LayerLinearGrid.vue'
import LayerRandomGrid from '@/components/molecules/LayerRandomGrid.vue'
import { globalInjectionKeys } from '@/main'

const visible = ref(true)
const leaving = ref(false)
const minDuration = 2000

export const loaderVisible = readonly(visible)
export const loaderLeaving = readonly(leaving)

export type SiteLoaderPublicInstance = {
  readonly leaving: Ref<boolean>
  readonly visible: Ref<boolean>
  show: (onEntered?: () => void) => Promise<gsap.core.Timeline>
  hide: () => Promise<gsap.core.Timeline>
}

export default defineComponent({
  name: 'SiteLoader',
  components: {
    LayerLinearGrid,
    LayerRandomGrid,
    Logo,
    OverlayTexts
  },
  setup (props, { attrs, slots, emit, expose }) {
    const preloader = inject(globalInjectionKeys.preloader)
    const grid = templateRef<ComponentPublicInstance<typeof LayerLinearGrid>>('grid')
    const wrapper = templateRef<HTMLElement>('wrapper')
    const content = templateRef<HTMLElement>('content')
    const liteMode = ref(false)
    const elapsedMinDuration = ref(false)

    let firstAccess = true
    let pendingShow = false
    let pendingHide = false

    const showTimeline = gsap.timeline({
      onComplete: () => pendingHide && hide(),
      onStart: () => (visible.value = true)
    })

    const hideTimeline = gsap.timeline({
      onComplete: () => pendingShow && show()
    })

    const show = (onEntered?: () => void) => {
      if ((hideTimeline.progress() < 1 && hideTimeline.duration() > 0) || !grid.value?.ready) {
        pendingShow = true
        // pendingHide = false
        return
      }

      pendingShow = false
      showTimeline.clear()
      showTimeline.to(wrapper.value, { autoAlpha: 1, duration: 0.01 })
      showTimeline.add(grid.value.show({ duration: firstAccess ? 0.01 : 0.35, onComplete: onEntered }))
      showTimeline.to(content.value, { autoAlpha: 1, duration: 0.2 })
      if (firstAccess) {
        showTimeline.add(() => {
          preloader.visible = false
          document.body.style.background = '#000'
        }, '<')
        showTimeline.to({ value: 0 }, { value: 1, duration: 1 })
      }

      return showTimeline
    }

    const hide = async () => {
      if ((showTimeline.progress() < 1 && showTimeline.duration() > 0) || !grid.value?.ready || !elapsedMinDuration.value) {
        // pendingShow = false
        pendingHide = true
        return
      }

      pendingHide = false
      hideTimeline.clear()
      hideTimeline.add(() => (leaving.value = true))
      hideTimeline.to(content.value, { autoAlpha: 0, duration: firstAccess ? 0.5 : 0.3 })
      hideTimeline.add(grid.value.hide({ duration: firstAccess ? 0.8 : 0.5 }))
      hideTimeline.to(wrapper.value, { autoAlpha: 0, duration: 0.1 })
      hideTimeline.add(() => {
        liteMode.value = true
        visible.value = false
        leaving.value = false
      }, 0.45)

      firstAccess = false
      return hideTimeline
    }

    onMounted(() => setTimeout(() => (elapsedMinDuration.value = true), minDuration))

    watch(() => grid.value?.ready, async ready => {
      if (!ready) return
      if (pendingShow) {
        show()
      } else if (pendingHide) {
        hide()
      }
    })

    watch(elapsedMinDuration, elapsed => {
      if (elapsed && pendingHide) hide()
    })

    expose({
      leaving: loaderLeaving,
      visible: loaderVisible,
      show,
      hide
    })

    return {
      liteMode
    }
  }
})
</script>

<template>
  <div ref="wrapper" class="wrapper fixed inset-0">
    <LayerLinearGrid
      ref="grid"
      :square-size="'auto'"
      :duration="1"
      :default-visible="true"
      class="!absolute inset-0 c-blue"
    />
    <div ref="content" class="wrapper-content w-full h-full flex items-center justify-center">
      <OverlayTexts class="absolute inset-0 p-20" :list="['IMMERSIVE EXPERIENCE', 'experimental Sensory hub', 'Installation']" />
      <span class="label1 c-black absolute top-4 right-4 text-right">
        -<br />
        ><br />
        -<br />
        ----
      </span>
      <div class="content relative text-center c-black">
        <div class=" mx-auto w-20 h-20 mb-4">
          <LayerRandomGrid :square-size="20" />
        </div>
        <Transition mode="out-in">
          <div v-if="!liteMode" class="text-center">
            <Logo class="w-40 h-12 mx-auto" />
            <span>{ creative innovation studio }</span>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper, .wrapper-content {
  opacity: 0;
  visibility: hidden;
}

.wrapper {
  z-index: $z-loader;
}

.content {
  width: 90%;
  max-width: rem(590px);
}
</style>
